import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';
import { totalPrice as calculatePrice } from './PricingEngine';

function PrintOnly({ image, onEditImage }) {
  const userImage = image || localStorage.getItem('opticon_last_image');
  const [printType, setPrintType] = useState('canvas');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const canvasRef = useRef(null);
  const DPI = 300;
  const [imageSizeCm, setImageSizeCm] = useState(null);

  const printOptions = [
    { id: 'lustre', label: 'Lustre 300 GSM' },
    { id: 'light-art', label: 'Light Art Paper (210 GSM)' },
    { id: 'canvas', label: 'Canva (Satin)' },
    { id: 'entrada', label: 'Entrada Cotton Natural Rag 290 GSM' }
  ];

  /* ===== SIZE BASED HARDCODED PRICING ===== */
  const PRICE_TABLE = {
    '6x4': { standard: 1.00, entrada: 3.00 },
    '5x7': { standard: 3.95, entrada: 8.00 },
    '6x8': { standard: 5.95, entrada: 10.00 },
    '8x10': { standard: 12.95, entrada: 25.00 },
    '8x12': { standard: 14.95, entrada: 30.00 },
    '10x12': { standard: 18.95, entrada: 35.00 },
    '11x14': { standard: 19.95, entrada: 40.00 },
    '12x16': { standard: 24.95, entrada: 50.00 },
    '12x18': { standard: 24.95, entrada: 50.00 },
    '16x20': { standard: 39.95, entrada: 80.00 },
    '16x24': { standard: 39.95, entrada: 80.00 },
    '20x24': { standard: 59.95, entrada: 120.00 },
    '20x30': { standard: 69.95, entrada: 140.00 },
    '24x30': { standard: 75.00, entrada: 150.00 },
    '24x36': { standard: 85.00, entrada: 170.00 },
    '30x40': { standard: 140.00, entrada: 280.00 },
    '40x60': { standard: 230.00, entrada: 460.00 }
  };

  const widthCm = Number(imageSizeCm?.widthCm || 0);
  const heightCm = Number(imageSizeCm?.heightCm || 0);

  // Convert cm → inches
  const widthIn = Math.round(widthCm / 2.54);
  const heightIn = Math.round(heightCm / 2.54);

  const normalizedSize = `${widthIn}x${heightIn}`;

  const getPrintPrice = () => {
    const sizes = [
      [6, 4],
      [5, 7],
      [6, 8],
      [8, 10],
      [8, 12],
      [10, 12],
      [11, 14],
      [12, 16],
      [12, 18],
      [16, 20],
      [16, 24],
      [20, 24],
      [20, 30],
      [24, 30],
      [24, 36],
      [30, 40],
      [40, 60]
    ];

    let w = Math.max(widthIn, heightIn);
    let h = Math.min(widthIn, heightIn);

    // Smaller than minimum → force 6x4
    if (w <= 6 && h <= 4) {
      const sizeData = PRICE_TABLE['6x4'];
      return printType === 'entrada'
        ? sizeData.entrada
        : sizeData.standard;
    }

    // Larger than maximum → force 40x60
    if (w >= 40 || h >= 60) {
      const sizeData = PRICE_TABLE['40x60'];
      return printType === 'entrada'
        ? sizeData.entrada
        : sizeData.standard;
    }

    // Find nearest supported size
    let selected = sizes[sizes.length - 1];

    for (const [sw, sh] of sizes) {
      if (w <= sw && h <= sh) {
        selected = [sw, sh];
        break;
      }
    }

    const key = `${selected[0]}x${selected[1]}`;
    const sizeData = PRICE_TABLE[key];

    console.log({
      uploaded: `${w}x${h}`,
      selected: key,
      printType,
      price: printType === 'entrada'
        ? sizeData.entrada
        : sizeData.standard
    });

    return printType === 'entrada'
      ? sizeData.entrada
      : sizeData.standard;
  };

  const totalPriceValue = Number(getPrintPrice()) || 0;

  const GST_RATE = 0.1; // 10%
  const gstAmount = totalPriceValue * GST_RATE;
  const totalWithGST = totalPriceValue + gstAmount;
  /* ===== GST + Total ===== */

  const getFilterStyle = () => {
    switch (printType) {
      case 'lustre':
        return { filter: 'contrast(1.08) saturate(1.1)' };

      case 'light-art':
        return { filter: 'contrast(1.02) saturate(0.95)' };

      case 'canvas':
        return { filter: 'contrast(1.05) saturate(1.08)' };

      case 'entrada':
        return { filter: 'contrast(0.98) saturate(0.9) brightness(1.02)' };

      default:
        return {};
    }
  };

  /* ===== IMAGE TO BASE64 ===== */
  const convertImageToBase64 = async (url) => {
    if (!url) return null;

    const res = await fetch(url);
    const blob = await res.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  /* ================= ADD TO CART ================= */
  const handleAddToCart = async () => {
    if (!userImage) {
      alert("Please upload an image first.");
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      // ✅ ALWAYS upload cropped user image (NOT canvas)
      const resImg = await fetch(userImage, { cache: 'no-store' });
      const blob = await resImg.blob();

      const uploadedImageBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });


      const payload = {
        id: 8430,
        quantity: 1,
        meta_data: [
          { key: 'print_type', value: printType },
          { key: 'print_price', value: totalWithGST.toFixed(2) },
          { key: 'print_base_price', value: totalPriceValue },
          { key: 'edited_image', value: uploadedImageBase64 },
        ]
      };
      console.log('Payload for API:', payload);
      const response = await axios.post(
        `https://opticonframing.com.au/wp-json/opticon/v1/print-only/add-to-cart`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const percent = Math.round((e.loaded * 100) / e.total);
            setProgress(percent);   // ✅ real dynamic %
          },
        }
      );

      if (response.data.success) {
        setProgress(100);

        setTimeout(() => {
          window.location.href = 'https://opticonframing.com.au/cart';
        }, 700);
      } else {
        throw new Error(response.data.error || 'Server error');
      }

    } catch (err) {
      console.error('❌ Error:', err);
      alert('Failed to add to cart. Check console for details.');
      setLoading(false);
      setProgress(0);
    }
  };

  useEffect(() => {
    if (!userImage || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const filterStyle = getFilterStyle();
      ctx.filter = filterStyle.filter || 'none';

      const canvasRatio = canvas.width / canvas.height;
      const imgRatio = img.width / img.height;
      let drawWidth, drawHeight, offsetX, offsetY;

      if (imgRatio > canvasRatio) {
        drawWidth = canvas.width;
        drawHeight = canvas.width / imgRatio;
        offsetX = 0;
        offsetY = (canvas.height - drawHeight) / 2;
      } else {
        drawHeight = canvas.height;
        drawWidth = canvas.height * imgRatio;
        offsetY = 0;
        offsetX = (canvas.width - drawWidth) / 2;
      }
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      ctx.filter = 'none';
    };
    img.src = userImage;
  }, [userImage, printType]);

  useEffect(() => {
    if (!userImage) {
      window.location.href = '/';   // force upload flow
    }
  }, [userImage]);
  console.log('Stored image:', localStorage.getItem('opticon_last_image'));

  useEffect(() => {
    if (!userImage) return;

    const img = new Image();
    img.onload = () => {
      const widthCm = ((img.width / DPI) * 2.54).toFixed(1);
      const heightCm = ((img.height / DPI) * 2.54).toFixed(1);

      setImageSizeCm({
        widthCm,
        heightCm,
        widthPx: img.width,
        heightPx: img.height
      });
    };

    img.src = userImage;
  }, [userImage]);


  return (
    <div className="product-page">

      {/* ===== UPDATED LOADER WITH REQUESTED COLORS ===== */}
      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          {/* Circular Loader */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF', // Requested Color
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          {/* Percentage Text */}
          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            {progress}%
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* LEFT PREVIEW */}
      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          width={680}
          height={460}
          className="real-canvas-preview"
        />
      </div>

      {/* RIGHT : PRODUCT INFO */}
      <div className="product-info-panel">

        <h1 className="product-title">Print Only</h1>

        <div className="print-description">
          {printType === 'lustre' && 'Premium lustre finish with vibrant color reproduction.'}
          {printType === 'light-art' && 'Elegant light art paper with soft matte finish.'}
          {printType === 'canvas' && 'Satin canvas print with rich texture and depth.'}
          {printType === 'entrada' && 'Museum-grade Entrada Cotton Natural Rag archival print.'}
        </div>

        {/* Image Size Info */}
        {imageSizeCm && (
          <div className="image-size-row">
            <span className="form-label">Image Size</span>
            <span className="image-size-value">
              {imageSizeCm.widthCm}×{imageSizeCm.heightCm} cm
            </span>

            <button
              type="button"
              className="edit-image-btn"
              onClick={onEditImage}
              title="Edit image"
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          </div>
        )}

        {/* Printing option row */}
        <div className="print-option-row">

          <label className="form-label">Printing Options</label>

          <select
            className="form-select"
            value={printType}
            onChange={(e) => setPrintType(e.target.value)}
          >
            {printOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Price + Add to Cart */}
        <div className="price-cart-row">
          <div className="product-price">
            ${totalPriceValue.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)}= ${totalWithGST.toFixed(2)}
          </div>

          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? `${progress}%` : 'Add to Cart'}
          </button>
        </div>

      </div>
    </div>
  );
}

export default PrintOnly;